import type { GlycemicClass, NutritionFacts, NutritionRow, Unit } from './types';

export interface NutritionTotals extends NutritionFacts {
  glycemicLoad: number;
}

/**
 * DE: Typischer glykämischer Index je Klasse, Grundlage der glykämischen Last.
 * EN: Typical glycemic index per class, used for the glycemic load.
 */
export const GI_VALUE: Readonly<Record<GlycemicClass, number>> = {
  none: 0,
  low: 35,
  medium: 60,
  high: 80,
};

const FIELDS = ['kcal', 'protein', 'fat', 'carbs', 'fibre', 'sugar', 'glycemicLoad'] as const;

/**
 * DE: Nährwerte, die alle bei null beginnen.
 * EN: Nutrition totals that all start at zero.
 */
export function emptyNutrition(): NutritionTotals {
  return { kcal: 0, protein: 0, fat: 0, carbs: 0, fibre: 0, sugar: 0, glycemicLoad: 0 };
}

function toGrams(row: NutritionRow, unit: Unit, quantity: number): number {
  if (unit !== 'pcs') return quantity;
  if (!row.gramsPerPiece) throw new Error('gramsPerPiece is required for products in pieces');
  return quantity * row.gramsPerPiece;
}

/**
 * DE: Nährwerte und glykämische Last einer Menge (ml zählt wie g, Stück über das Stückgewicht).
 * EN: Nutrition and glycemic load of a quantity (ml counts as g, pieces via the piece weight).
 */
export function nutritionOf(row: NutritionRow, unit: Unit, quantity: number): NutritionTotals {
  const factor = toGrams(row, unit, quantity) / 100;
  const carbs = row.carbs * factor;
  return {
    kcal: row.kcal * factor,
    protein: row.protein * factor,
    fat: row.fat * factor,
    carbs,
    fibre: row.fibre * factor,
    sugar: row.sugar * factor,
    glycemicLoad: (GI_VALUE[row.gi] * carbs) / 100,
  };
}

/**
 * DE: Zwei Nährwertsummen addieren.
 * EN: Add two nutrition totals.
 */
export function addNutrition(a: NutritionTotals, b: NutritionTotals): NutritionTotals {
  const sum = emptyNutrition();
  for (const field of FIELDS) sum[field] = a[field] + b[field];
  return sum;
}

/**
 * DE: Alle Nährwerte mit einem Faktor multiplizieren, z. B. für eine Portion.
 * EN: Multiply all nutrition values by a factor, e.g. for one portion.
 */
export function scaleNutrition(totals: NutritionTotals, factor: number): NutritionTotals {
  const scaled = emptyNutrition();
  for (const field of FIELDS) scaled[field] = totals[field] * factor;
  return scaled;
}
